import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { Firestore, doc } from "@angular/fire/firestore";
import { AuthStateService } from "@core/auth-state.service";
import { LottieComponent, AnimationOptions } from "ngx-lottie";
import arrowToInboxAnimation from "../../../assets/lotti_animation/header_animation/arrow_to_inbox.json";
import { docData } from "rxfire/firestore";
import { DocumentData } from "rxfire/firestore/interfaces";
import { Subject, Subscription, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from "rxjs";
import { CommonService } from "src/app/services/common/common.service";

interface TitleAnimation {
  show?: boolean;
  text?: string;
  text_colour?: string;
  lottie_url?: string;
  updatedAt?: number;
}

@Component({
  selector: "app-inbox-title-nudge",
  standalone: true,
  imports: [CommonModule, LottieComponent],
  templateUrl: "./inbox-title-nudge.component.html",
  styleUrls: ["./inbox-title-nudge.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxTitleNudgeComponent implements OnInit, OnDestroy {
  isActive = false;
  phase: "text" | "arrow" | "hidden" = "hidden";
  text = "";
  textColor = "#E5242A";
  // Use JSON round-trip clone to prevent lottie-web from mutating the shared import object.
  // JSON.parse/stringify is used over structuredClone for universal compatibility (SSR, older runtimes).
  arrowOptions: AnimationOptions = { animationData: JSON.parse(JSON.stringify(arrowToInboxAnimation)) };

  private subscription: Subscription | null = null; // if you keep manual unsubscribe
// or remove the field entirely if you rely solely on takeUntil
  private static readonly MAX_TEXT_LEN = 15;
  private static readonly TEXT_PHASE_MS = 4000;
  private static readonly ARROW_PHASE_MS = 2000;
  private loopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private destroyed = new Subject<void>();
  constructor(
    private firestore: Firestore,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private authStateService: AuthStateService,
  ) {}

  ngOnInit(): void {
    void this.initAsync();
  }
  
private initAsync(): void {
  this.commonService.getUid("").pipe(
    take(1), // Only take the first emitted value
    filter((userId): userId is string => !!userId), // Only proceed if userId is valid
    // Gate on customTokenReady$ before opening the Firestore listener so it never
    // fires before the Firebase custom token is confirmed stored, preventing the
    // permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
    switchMap(userId =>
      this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const ref = doc(this.firestore, `users/${userId}/in_app_notification/status`);
          return docData(ref).pipe(
            map((d: DocumentData | undefined) => d?.title_animation as TitleAnimation | undefined),
            distinctUntilChanged((a, b) =>
              a?.show === b?.show && a?.text === b?.text && a?.text_colour === b?.text_colour
            )
          );
        })
      )
    ),
    // Tear down the whole pipeline (including while awaiting uid / customTokenReady$)
    // when the component is destroyed, so no Firestore listener can open post-destroy.
    takeUntil(this.destroyed)
  ).subscribe({
    next: (t: TitleAnimation) => {
      const text = (t?.text ?? '').trim();
      const validText = text.length > 0 && text.length <= InboxTitleNudgeComponent.MAX_TEXT_LEN;
      const shouldShow = !!t?.show && validText;

      if (!shouldShow) {
        this.stopLoop();
        this.phase = "hidden";
        this.cdr.markForCheck();
        return;
      }

      this.text = text;
      this.textColor = t?.text_colour || '#E5242A';
      if (!this.isActive) {
        this.startLoop();
      }
      this.cdr.markForCheck();
    },
    error: (err) => {
      console.error('[initAsync] Error:', err);
    }
  });
}

  private startLoop(): void {
    this.isActive = true;
    this.phase = "text";
    this.scheduleNext();
    this.cdr.markForCheck();
  }

  private scheduleNext(): void {
  this.clearTimer();
  const durationMs = this.phase === 'text'
    ? InboxTitleNudgeComponent.TEXT_PHASE_MS
    : InboxTitleNudgeComponent.ARROW_PHASE_MS;
    this.loopTimeoutId = setTimeout(() => {
      if (!this.isActive) return;
      this.phase = this.phase === "text" ? "arrow" : "text";
      this.scheduleNext();
      this.cdr.markForCheck();
    }, durationMs);
}

  private stopLoop(): void {
    this.isActive = false;
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.loopTimeoutId) {
      clearTimeout(this.loopTimeoutId);
      this.loopTimeoutId = null;
    }
  }

ngOnDestroy(): void {
  // Stop internal loops/timers/animation item first
  this.stopLoop();
  // Signal takeUntil to complete streams that used it
  this.destroyed.next();
  this.destroyed.complete();
  // legacy unsubscribe (safe even if takeUntil handled it)
  if (this.subscription) {
    this.subscription.unsubscribe();
    this.subscription = null;
  }
}
}


